import { useLayoutEffect, useRef, useState } from "react";
import { createRoot } from "react-dom/client";
import cssText from "./XsmbV4Light.shadow.css";
import markup from "./markup";
import useXsmbV4Light from "./useXsmbV4Light";
import { resolveConfigDow } from "./dow";

const HOST_STYLE = {
  all: "initial",
  display: "block",
  width: "100%",
  maxWidth: "100%",
  margin: 0,
  padding: 0,
  border: 0,
  background: "transparent",
  boxSizing: "border-box",
  lineHeight: "normal",
  color: "#000",
  fontFamily: '"Arial, Tahoma, Helvetica, Lexend Deca", sans-serif',
  fontSize: "15px",
};

function paintDigits(form) {
  const block = form.closest(".one-city");
  if (!block) return;
  const checked = form.querySelector("input:checked");
  const count = Number(checked?.value || 0);
  block.querySelectorAll("[data-nc]").forEach((node) => {
    const full = node.getAttribute("data-full") || node.textContent.trim();
    if (!node.getAttribute("data-full")) node.setAttribute("data-full", full);
    node.textContent = count ? full.slice(-count) : full;
  });
}

function readDigits(root) {
  return [...root.querySelectorAll("form.digits-form")].map((form) => form.querySelector("input:checked")?.value || "0");
}

function writeDigits(root, values) {
  root.querySelectorAll("form.digits-form").forEach((form, index) => {
    const value = values[index] || "0";
    const input = form.querySelector(`input[value="${value}"]`);
    if (input) input.checked = true;
    paintDigits(form);
  });
}

const TAB_DOW = ["all", 1, 2, 3, 4, 5, 6, 0];

function tabDow(link) {
  const item = link.closest(".tab-panel li");
  const panel = link.closest(".tab-panel");
  if (!item || !panel) return null;
  const marked = item.getAttribute("data-dow");
  if (marked != null) return resolveConfigDow(marked);
  const index = [...panel.querySelectorAll("li")].indexOf(item);
  return index < 0 ? null : TAB_DOW[index] ?? null;
}

function markActive(root, dow) {
  root.querySelectorAll(".tab-panel li").forEach((item, index) => {
    const marked = item.getAttribute("data-dow");
    const value = marked != null ? resolveConfigDow(marked) : TAB_DOW[index];
    item.classList.toggle("active", value === dow);
  });
}

function XsmbV4Light({ dow: dowProp }) {
  const [dow, setDow] = useState(() => resolveConfigDow(dowProp));
  const view = useXsmbV4Light(dow);
  const rootRef = useRef(null);

  useLayoutEffect(() => {
    const root = rootRef.current;
    if (!root) return undefined;
    const onChange = (event) => {
      const form = event.target.closest?.("form.digits-form");
      if (form && root.contains(form)) paintDigits(form);
    };
    const onClick = (event) => {
      const link = event.target.closest?.("a");
      if (!link || !root.contains(link) || !link.closest(".tab-panel")) return;
      const next = tabDow(link);
      if (next == null) return;
      event.preventDefault();
      setDow((current) => (current === next ? current : next));
      markActive(root, next);
    };
    root.addEventListener("change", onChange);
    root.addEventListener("click", onClick);
    return () => {
      root.removeEventListener("change", onChange);
      root.removeEventListener("click", onClick);
    };
  }, []);

  useLayoutEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    if (!view.html) {
      if (!root.dataset.live) {
        root.innerHTML = markup;
        writeDigits(root, []);
      }
      markActive(root, view.dow);
      return;
    }
    const digits = readDigits(root);
    root.innerHTML = view.html;
    root.dataset.live = "1";
    writeDigits(root, digits);
  }, [view.html, view.dow]);

  return <div className="xsmb4" ref={rootRef} />;
}

function XsmbV4LightHost({ dow, config }) {
  const hostRef = useRef(null);
  const reactRootRef = useRef(null);
  const resolved = dow != null && dow !== "" ? dow : config?.dow;

  useLayoutEffect(() => {
    const host = hostRef.current;
    if (!host) return undefined;
    const shadow = host.shadowRoot ?? host.attachShadow({ mode: "open" });
    let style = shadow.querySelector("style[data-xsmb4]");
    if (!style) {
      style = document.createElement("style");
      style.setAttribute("data-xsmb4", "");
      shadow.appendChild(style);
    }
    style.textContent = cssText;
    let mount = shadow.querySelector("[data-xsmb4-mount]");
    if (!mount) {
      mount = document.createElement("div");
      mount.setAttribute("data-xsmb4-mount", "");
      shadow.appendChild(mount);
    }
    if (!reactRootRef.current) reactRootRef.current = createRoot(mount);
    reactRootRef.current.render(<XsmbV4Light dow={resolved} />);
    return () => {
      reactRootRef.current?.unmount();
      reactRootRef.current = null;
    };
  }, [resolved]);

  return <div ref={hostRef} className="xsmb4-host" style={HOST_STYLE} />;
}

export default XsmbV4LightHost;
